import { DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useMemo, useState } from "react"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { DataInputDialog } from "lib/@dialogs/data-input-dialog"
import { AuthoringBoundTextEditor } from "authoring/components/authoring-editor"
import { macroNameYup } from "authoring/routes/macro/lib/macro-utils"
import { htmlToText } from "lib/html-to-text"
import { mergeMacroCategoryOptions, toggleCategory } from "authoring/routes/macro/lib/macro-category-utils"
import { MacroCategorySelector } from "./macro-category-selector"
import { MacroCategoryChips } from "./macro-category-chips"

export function CreateMacroDialog({ categories = [] }) {
    const [selectedCategories, setSelectedCategories] = useBoundValue("categories", [])
    const [touchedName, setTouchedName] = useState(false)
    const [touchedContent, setTouchedContent] = useState(false)
    const categoryOptions = useMemo(
        () => mergeMacroCategoryOptions(categories, selectedCategories),
        [categories, selectedCategories]
    )

    return (
        <DataInputDialog
            initialValue={{ name: "", content: "", categories: [] }}
            okCaption="Create Macro"
            okIcon="ic:round-add"
        >
            <DialogTitle data-cy="create-macro-title">Create Macro</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1.5, minWidth: { xs: 1, sm: 520 } }} data-cy="create-macro-dialog">
                    <Stack spacing={0.5}>
                        <BoundTextField
                            field="name"
                            label="Macro Name"
                            placeholder="example-macro"
                            size="small"
                            yup={macroNameYup}
                            omit={touchedName ? [] : ["error", "helperText"]}
                            onBlur={handleNameBlur}
                        />
                    </Stack>
                    <Stack spacing={1} data-cy="create-macro-content">
                        <Typography variant="subtitle2">Content</Typography>
                        <AuthoringBoundTextEditor
                            field="content"
                            label=""
                            workplaceMaxHeight={260}
                            yup="string.trim.required"
                            yupTransform={htmlToText}
                            omit={touchedContent ? [] : ["error", "helperText"]}
                            onBlur={handleContentBlur}
                        />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography variant="subtitle2">Categories</Typography>
                        <MacroCategorySelector
                            categories={categoryOptions}
                            selectedCategories={selectedCategories}
                            onChange={handleCategoryChange}
                        />
                        <MacroCategoryChips
                            categories={selectedCategories}
                            onRemove={handleCategoryRemove}
                            emptyMessage="No categories yet."
                        />
                    </Stack>
                </Stack>
            </DialogContent>
        </DataInputDialog>
    )

    function handleCategoryChange(values) {
        setSelectedCategories(values)
    }

    function handleCategoryRemove(category) {
        const updatedCategories = toggleCategory(selectedCategories, category)
        setSelectedCategories(updatedCategories)
    }

    function handleNameBlur() {
        setTouchedName(true)
    }

    function handleContentBlur() {
        setTouchedContent(true)
    }
}
